/**
 * settingsStore — paramètres utilisateur configurables de UNIQUE.
 *
 * Contient :
 *   - Délais d'escalade par quadrant (en jours de retard)
 *   - Seuils de détection d'urgence et d'importance (en heures d'échéance)
 *   - Informations du supérieur hiérarchique N+1 (email + nom)
 *   - Durée par défaut d'une session focus
 *   - Mots-clés qui déclenchent automatiquement "urgent" ou "important"
 *     dans les emails entrants (matching insensible à la casse)
 *
 * Persistance : localStorage via zustand/persist (clé "unique-settings").
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Quadrant } from './types';

export interface EscalationDelays {
  /** Délai en JOURS de retard avant escalade, par quadrant. */
  Q1: number; // urgent + important
  Q2: number; // non urgent + important
  Q3: number; // urgent + non important
  Q4: number; // non urgent + non important
}

export interface UrgencyThresholds {
  /** Une échéance ≤ N heures est considérée URGENTE. */
  urgentWithinHours: number;
  /** Une échéance ≤ N heures est considérée IMPORTANTE (priorité élevée). */
  importantWithinHours: number;
}

export interface Supervisor {
  name: string;
  email: string;
}

export interface KeywordRules {
  /** Mots-clés qui déclenchent "urgent" dans un email. */
  urgentKeywords: string[];
  /** Mots-clés qui déclenchent "important". */
  importantKeywords: string[];
}

export interface UniqueSettings {
  escalationDelays: EscalationDelays;
  urgencyThresholds: UrgencyThresholds;
  supervisor: Supervisor;
  keywords: KeywordRules;
  /** Durée par défaut d'une session focus (minutes). */
  defaultFocusMinutes: number;
  /** Activation globale de l'escalade automatique. */
  autoEscalationEnabled: boolean;
}

export const DEFAULT_SETTINGS: UniqueSettings = {
  escalationDelays: {
    Q1: 2, // urgent + important → 2 jours
    Q2: 4, // non urgent + important → 4 jours
    Q3: 3, // urgent + non important → 3 jours
    Q4: 5, // ni urgent ni important → 5 jours
  },
  urgencyThresholds: {
    urgentWithinHours: 8,
    importantWithinHours: 48,
  },
  supervisor: {
    name: '',
    email: '',
  },
  keywords: {
    urgentKeywords: ['urgent', 'asap', 'immédiatement', 'critique', 'deadline'],
    importantKeywords: ['important', 'priorité', 'essentiel', 'stratégique'],
  },
  defaultFocusMinutes: 25,
  autoEscalationEnabled: true,
};

interface SettingsState extends UniqueSettings {
  updateEscalationDelay: (quadrant: Quadrant, days: number) => void;
  updateUrgencyThreshold: (key: keyof UrgencyThresholds, hours: number) => void;
  updateSupervisor: (patch: Partial<Supervisor>) => void;
  setKeywords: (key: keyof KeywordRules, keywords: string[]) => void;
  setDefaultFocusMinutes: (minutes: number) => void;
  setAutoEscalationEnabled: (enabled: boolean) => void;
  resetToDefaults: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...DEFAULT_SETTINGS,

      updateEscalationDelay: (quadrant, days) =>
        set((state) => ({
          escalationDelays: {
            ...state.escalationDelays,
            [quadrant]: Math.max(0, Math.round(days)),
          },
        })),

      updateUrgencyThreshold: (key, hours) =>
        set((state) => ({
          urgencyThresholds: {
            ...state.urgencyThresholds,
            [key]: Math.max(0, Math.round(hours)),
          },
        })),

      updateSupervisor: (patch) =>
        set((state) => ({
          supervisor: { ...state.supervisor, ...patch },
        })),

      setKeywords: (key, keywords) =>
        set((state) => ({
          keywords: { ...state.keywords, [key]: keywords },
        })),

      setDefaultFocusMinutes: (minutes) =>
        set({ defaultFocusMinutes: Math.max(5, Math.round(minutes)) }),

      setAutoEscalationEnabled: (enabled) =>
        set({ autoEscalationEnabled: enabled }),

      resetToDefaults: () => set({ ...DEFAULT_SETTINGS }),
    }),
    {
      name: 'unique-settings',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
